"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import {
  Check,
  FileText,
  Mail,
  MessageCircle,
  MessagesSquare,
  Mic,
  Phone,
  Video,
} from "lucide-react";

type CapabilitySection = {
  id: string;
  title: string;
  description: string;
  bullets: string[];
  reverse: boolean;
  visual: "voice-chat" | "trained" | "channels" | "tools" | "team";
};

const SECTIONS: CapabilitySection[] = [
  {
    id: "human-like-agents",
    title: "Human-like voice and chat agents",
    description:
      "Warm, expressive, and quick on their feet — agents that carry a real conversation, not a phone tree or chatbot script.",
    bullets: [
      "Speaks and writes with real timing, tone, and warmth.",
      "Handles phone and live chat from the same agent profile.",
      "Build and launch one right from your browser.",
    ],
    reverse: false,
    visual: "voice-chat",
  },
  {
    id: "trained-on-your-data",
    title: "Trained on your data",
    description:
      "Hand your agent the docs you already have — company profile, FAQs, policies, and knowledge bases — so answers stay on-brand.",
    bullets: [
      "Company profile, services, and brand voice as the foundation.",
      "Upload PDFs, Markdown, and help-center content.",
      "RAG search so the agent finds the right answer mid-conversation.",
    ],
    reverse: true,
    visual: "trained",
  },
  {
    id: "any-channel",
    title: "Conversations on any channel",
    description:
      "Meet people where they already reach out — phone, website chat, text, email, browser calls, and more.",
    bullets: [
      "One agent across live chat, phone, SMS, and email.",
      "Browser and video calls when a face-to-face moment helps.",
      "Consistent personality and knowledge on every channel.",
    ],
    reverse: false,
    visual: "channels",
  },
  {
    id: "your-tools",
    title: "Your tools, built in",
    description:
      "Inbound GPT plugs into the apps your team already uses — scheduling, EMR, and custom MCP workflows.",
    bullets: [
      "Connect Google Calendar, Calendly, Zoom, and more.",
      "Book in Juvonno, NexHealth, and other practice systems.",
      "Wire custom MCPs for CRMs, databases, and internal tools.",
    ],
    reverse: true,
    visual: "tools",
  },
  {
    id: "team-in-the-loop",
    title: "Your team, always in the loop",
    description:
      "When a conversation needs a person, your agent hands off cleanly — with context, whisper messages, and a full inbox trail.",
    bullets: [
      "Live inbox with transcripts across every channel.",
      "Warm transfers with whisper context for staff.",
      "Conversation summaries and alerts so nothing falls through.",
    ],
    reverse: false,
    visual: "team",
  },
];

const PROFILE_AVATARS = [
  { src: "/images/profiles/Katie.png", name: "Katie" },
  { src: "/images/profiles/Mia.png", name: "Mia" },
  { src: "/marketing/trish-onboarding-agent.png", name: "Trish" },
  { src: "/images/profiles/Leo.png", name: "Leo" },
] as const;

const CHANNELS = [
  { icon: Phone, label: "Phone" },
  { icon: MessageCircle, label: "Live chat" },
  { icon: MessagesSquare, label: "SMS" },
  { icon: Mail, label: "Email" },
  { icon: Mic, label: "Browser call" },
  { icon: Video, label: "Video" },
] as const;

function VoiceChatVisual() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-[#faf8f5] to-slate-100 p-6 shadow-lg sm:p-8">
      <div className="flex items-center gap-3">
        {PROFILE_AVATARS.map((avatar, i) => (
          <motion.div
            key={avatar.name}
            className="relative"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
            style={{ marginLeft: i === 0 ? 0 : -12 }}
          >
            <Image
              src={avatar.src}
              alt={avatar.name}
              width={56}
              height={56}
              className="h-14 w-14 rounded-full border-2 border-white object-cover shadow-md"
            />
          </motion.div>
        ))}
        <motion.div
          className="ml-2 flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.45 }}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          Live
        </motion.div>
      </div>

      <div className="mt-6 space-y-3">
        <motion.div
          className="max-w-[85%] rounded-2xl rounded-tl-md bg-white px-4 py-3 text-sm text-slate-700 shadow-sm ring-1 ring-slate-100"
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.25, duration: 0.45 }}
        >
          Thanks for calling. I have you on this number — is that correct?
        </motion.div>
        <motion.div
          className="ml-auto max-w-[75%] rounded-2xl rounded-tr-md bg-primary px-4 py-3 text-sm text-primary-foreground shadow-sm"
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.45 }}
        >
          Yes — I need to book an appointment for next week.
        </motion.div>
        <motion.div
          className="max-w-[90%] rounded-2xl rounded-tl-md bg-white px-4 py-3 text-sm text-slate-700 shadow-sm ring-1 ring-slate-100"
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.55, duration: 0.45 }}
        >
          Perfect. I can check availability and get you scheduled right now.
        </motion.div>
      </div>

      <motion.div
        className="mt-6 flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white"
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <Phone className="h-4 w-4 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">Inbound call · Online Support Agent</p>
          <p className="text-xs text-slate-400">Natural voice · sub-second replies</p>
        </div>
        <div className="flex gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.span
              key={i}
              className="w-0.5 rounded-full bg-primary"
              animate={{ height: [6, 14, 6] }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                delay: i * 0.12,
                ease: "easeInOut",
              }}
              style={{ height: 8 }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function PlatformImageVisual({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return (
    <motion.div
      className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-lg"
      initial={{ opacity: 0, scale: 0.97 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55 }}
    >
      <div className="relative aspect-[4/3] w-full bg-slate-50">
        <Image src={src} alt={alt} fill className="object-cover object-top" sizes="(max-width: 768px) 100vw, 50vw" />
      </div>
      {caption ? (
        <p className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">{caption}</p>
      ) : null}
    </motion.div>
  );
}

function TrainedVisual() {
  return (
    <div className="relative pb-8">
      <PlatformImageVisual
        src="/images/platform-education.png"
        alt="Train your agent with company knowledge"
        caption="Company profile · knowledge bases · RAG search"
      />
      <motion.div
        className="absolute bottom-2 left-3 right-3 flex flex-wrap justify-center gap-2 sm:left-6 sm:right-auto sm:justify-start"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.35 }}
      >
        {["Returns_Policy.pdf", "Help_Center.md", "Services.docx"].map((name, i) => (
          <motion.div
            key={name}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-md"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 3, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
          >
            <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="truncate">{name}</span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

function ChannelsVisual() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50 p-6 shadow-lg sm:p-8">
      <div className="mb-6 overflow-hidden rounded-xl border border-slate-200">
        <Image
          src="/images/platform-skills.png"
          alt="Multi-channel agent responsibilities"
          width={640}
          height={360}
          className="h-auto w-full object-cover"
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {CHANNELS.map((channel, i) => (
          <motion.div
            key={channel.label}
            className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white px-2 py-3 shadow-sm"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 + i * 0.06 }}
            whileHover={{ y: -3 }}
          >
            <channel.icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
            <span className="text-[11px] font-medium text-slate-600">{channel.label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function ToolsVisual() {
  return (
    <PlatformImageVisual
      src="/images/platform-tools.png"
      alt="Appointment tools and integrations"
      caption="Calendly · Google Calendar · Juvonno · NexHealth · MCP"
    />
  );
}

function TeamVisual() {
  return (
    <div className="space-y-4">
      <PlatformImageVisual
        src="/images/platform-inbox.png"
        alt="Unified conversation inbox"
      />
      <motion.div
        className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-md"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        <div className="relative aspect-[21/9] w-full">
          <Image
            src="/images/platform-team.png"
            alt="Team members always in the loop"
            fill
            className="object-cover object-top"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
        <p className="border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
          Handoffs · whisper transfers · email summaries
        </p>
      </motion.div>
    </div>
  );
}

function SectionVisual({ type }: { type: CapabilitySection["visual"] }) {
  switch (type) {
    case "voice-chat":
      return <VoiceChatVisual />;
    case "trained":
      return <TrainedVisual />;
    case "channels":
      return <ChannelsVisual />;
    case "tools":
      return <ToolsVisual />;
    case "team":
      return <TeamVisual />;
  }
}

export function HomeCapabilitySections() {
  return (
    <div data-testid="home-capability-sections">
      {SECTIONS.map((section, index) => {
        const cream = index % 2 === 0;
        return (
          <section
            key={section.id}
            id={section.id}
            className={`scroll-mt-20 border-b border-slate-200/80 py-20 md:py-28 ${
              cream ? "bg-[#faf8f5] dark:bg-slate-950" : "bg-white dark:bg-slate-900"
            }`}
          >
            <div className="container mx-auto max-w-6xl px-4">
              <div
                className={`flex flex-col items-center gap-12 lg:gap-16 ${
                  section.reverse ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
              >
                <motion.div
                  className="w-full flex-1"
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.55 }}
                >
                  <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl md:text-[2.75rem] md:leading-tight">
                    {section.title}
                  </h2>
                  <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-600 dark:text-slate-300">
                    {section.description}
                  </p>
                  <ul className="mt-8 space-y-3.5">
                    {section.bullets.map((bullet, i) => (
                      <motion.li
                        key={bullet}
                        className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-600 dark:text-slate-300"
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.12 + i * 0.08, duration: 0.4 }}
                      >
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <Check className="h-3 w-3 text-primary" strokeWidth={2.5} />
                        </span>
                        {bullet}
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div
                  className="w-full flex-1"
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: 0.08 }}
                >
                  <SectionVisual type={section.visual} />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
